import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form, Input, Button, Select, DatePicker, InputNumber, Space, Spin, message,
  Card, Table, Popconfirm, Typography,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  getOrder, createOrder, updateOrder,
  listOrderLineItems, createOrderLineItem, updateOrderLineItem, deleteOrderLineItem,
  type OrderLineItem,
} from '@/api/orderApi';
import { listCustomers, type Customer } from '@/api/customerApi';
import { useAuth } from '@/hooks/useAuth';

const { TextArea } = Input;

const ORDER_STATUSES = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'] as const;

interface LineItemRow {
  key: string;
  id?: string;
  product_name: string;
  description: string;
  quantity: number;
  unit_price: number;
  discount_percentage: number;
  line_total: number;
  sort_order: number;
  isNew?: boolean;
  isDirty?: boolean;
}

export default function OrderForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customerOptions, setCustomerOptions] = useState<{ value: string; label: string }[]>([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [lineItems, setLineItems] = useState<LineItemRow[]>([]);
  const [nextSortOrder, setNextSortOrder] = useState(1);

  const canWrite = hasPermission('orders', 'write');

  // Load customers for select
  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const result = await listCustomers({ from: 0, size: 20, keyword: customerSearch || undefined });
        setCustomerOptions(
          result.items.map((c: Customer) => ({
            value: c.id,
            label: `${c.name}${c.company ? ` (${c.company})` : ''}`,
          }))
        );
      } catch {
        // silently fail
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  // Load order data for edit
  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    Promise.allSettled([getOrder(id), listOrderLineItems(id)])
      .then(([orderResult, itemsResult]) => {
        if (orderResult.status === 'fulfilled') {
          const o = orderResult.value;
          form.setFieldsValue({
            customer_id: o.customer_id,
            contact_id: o.contact_id || undefined,
            quote_id: o.quote_id || undefined,
            order_number: o.order_number || undefined,
            title: o.title,
            description: o.description || undefined,
            status: o.status,
            order_date: o.order_date ? dayjs(o.order_date) : undefined,
            discount_percentage: o.discount_percentage,
            discount_amount: o.discount_amount,
            tax_percentage: o.tax_percentage,
            currency: o.currency,
            shipping_address: o.shipping_address || undefined,
            payment_terms: o.payment_terms || undefined,
            notes: o.notes || undefined,
            tags: o.tags?.length ? o.tags : undefined,
          });

          // Pre-populate customer option
          setCustomerOptions((prev) => {
            if (prev.find((opt) => opt.value === o.customer_id)) return prev;
            return [...prev, { value: o.customer_id, label: o.customer_id }];
          });
        }

        if (itemsResult.status === 'fulfilled') {
          const items = itemsResult.value;
          setLineItems(
            items.map((li: OrderLineItem) => ({
              key: li.id,
              id: li.id,
              product_name: li.product_name,
              description: li.description,
              quantity: li.quantity,
              unit_price: li.unit_price,
              discount_percentage: li.discount_percentage,
              line_total: li.line_total,
              sort_order: li.sort_order,
            }))
          );
          const maxOrder = items.length > 0
            ? Math.max(...items.map((li: OrderLineItem) => li.sort_order))
            : 0;
          setNextSortOrder(maxOrder + 1);
        }
      })
      .finally(() => setLoading(false));
  }, [id, isEdit, form]);

  const calcLineTotal = (qty: number, price: number, disc: number) =>
    Math.round(qty * price * (1 - disc / 100) * 100) / 100;

  const addLineItem = useCallback(() => {
    const newRow: LineItemRow = {
      key: `new_${Date.now()}`,
      product_name: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      discount_percentage: 0,
      line_total: 0,
      sort_order: nextSortOrder,
      isNew: true,
    };
    setLineItems((prev) => [...prev, newRow]);
    setNextSortOrder((n) => n + 1);
  }, [nextSortOrder]);

  const updateLineItemField = useCallback(
    (key: string, field: keyof LineItemRow, value: unknown) => {
      setLineItems((prev) =>
        prev.map((row) => {
          if (row.key !== key) return row;
          const updated = { ...row, [field]: value, isDirty: true };
          if (['quantity', 'unit_price', 'discount_percentage'].includes(field)) {
            updated.line_total = calcLineTotal(
              field === 'quantity' ? (value as number) : updated.quantity,
              field === 'unit_price' ? (value as number) : updated.unit_price,
              field === 'discount_percentage' ? (value as number) : updated.discount_percentage
            );
          }
          return updated;
        })
      );
    },
    []
  );

  const removeLineItem = useCallback(
    async (key: string) => {
      const row = lineItems.find((r) => r.key === key);
      if (row?.id && isEdit) {
        try {
          await deleteOrderLineItem(id, row.id);
        } catch (err) {
          message.error(err instanceof Error ? err.message : t('app.error'));
          return;
        }
      }
      setLineItems((prev) => prev.filter((r) => r.key !== key));
    },
    [lineItems, isEdit, id, t]
  );

  const handleSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      setSaving(true);
      try {
        const orderData = {
          customer_id: values.customer_id as string,
          contact_id: (values.contact_id as string) || undefined,
          quote_id: (values.quote_id as string) || undefined,
          order_number: (values.order_number as string) || undefined,
          title: values.title as string,
          description: (values.description as string) || undefined,
          status: isEdit ? (values.status as string) : undefined,
          order_date: values.order_date
            ? (values.order_date as dayjs.Dayjs).toISOString()
            : undefined,
          discount_percentage: (values.discount_percentage as number) || 0,
          discount_amount: (values.discount_amount as number) || 0,
          tax_percentage: (values.tax_percentage as number) || 0,
          currency: (values.currency as string) || 'TWD',
          shipping_address: (values.shipping_address as string) || undefined,
          payment_terms: (values.payment_terms as string) || undefined,
          notes: (values.notes as string) || undefined,
          tags: values.tags as string[] | undefined,
        };

        let orderId: string;
        if (isEdit) {
          await updateOrder(id, orderData);
          orderId = id;
        } else {
          const created = await createOrder(orderData);
          orderId = created.id;
        }

        // Save line items
        for (const row of lineItems) {
          const itemData = {
            product_name: row.product_name,
            description: row.description || undefined,
            quantity: row.quantity,
            unit_price: row.unit_price,
            discount_percentage: row.discount_percentage,
            sort_order: row.sort_order,
          };

          if (row.isNew) {
            await createOrderLineItem(orderId, itemData);
          } else if (row.isDirty && row.id) {
            await updateOrderLineItem(orderId, row.id, itemData);
          }
        }

        message.success(t(isEdit ? 'orders.update_success' : 'orders.create_success'));
        navigate(`/orders/${orderId}`);
      } catch (err) {
        message.error(err instanceof Error ? err.message : t('app.error'));
      } finally {
        setSaving(false);
      }
    },
    [isEdit, id, lineItems, navigate, t]
  );

  useEffect(() => {
    if (!canWrite) navigate('/orders');
  }, [canWrite, navigate]);

  if (!canWrite) return null;

  if (loading) return <Spin size="large" />;

  const lineItemColumns: ColumnsType<LineItemRow> = [
    {
      title: '#',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 60,
      render: (_val: number, _record: LineItemRow, index: number) => index + 1,
    },
    {
      title: t('orders.line_item.product_name'),
      dataIndex: 'product_name',
      key: 'product_name',
      render: (val: string, record: LineItemRow) => (
        <Input
          value={val}
          onChange={(e) => updateLineItemField(record.key, 'product_name', e.target.value)}
          placeholder={t('orders.line_item.product_name')}
        />
      ),
    },
    {
      title: t('orders.line_item.description'),
      dataIndex: 'description',
      key: 'description',
      render: (val: string, record: LineItemRow) => (
        <Input
          value={val}
          onChange={(e) => updateLineItemField(record.key, 'description', e.target.value)}
        />
      ),
    },
    {
      title: t('orders.line_item.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (val: number, record: LineItemRow) => (
        <InputNumber
          value={val}
          min={0.01}
          style={{ width: '100%' }}
          onChange={(v) => updateLineItemField(record.key, 'quantity', v ?? 1)}
        />
      ),
    },
    {
      title: t('orders.line_item.unit_price'),
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 120,
      render: (val: number, record: LineItemRow) => (
        <InputNumber
          value={val}
          min={0}
          style={{ width: '100%' }}
          onChange={(v) => updateLineItemField(record.key, 'unit_price', v ?? 0)}
        />
      ),
    },
    {
      title: t('orders.line_item.discount'),
      dataIndex: 'discount_percentage',
      key: 'discount_percentage',
      width: 100,
      render: (val: number, record: LineItemRow) => (
        <InputNumber
          value={val}
          min={0}
          max={100}
          formatter={(v) => `${v}%`}
          parser={(v) => Number((v ?? '').replace('%', ''))}
          style={{ width: '100%' }}
          onChange={(v) => updateLineItemField(record.key, 'discount_percentage', v ?? 0)}
        />
      ),
    },
    {
      title: t('orders.line_item.line_total'),
      dataIndex: 'line_total',
      key: 'line_total',
      width: 130,
      align: 'right',
      render: (val: number) => val?.toLocaleString(undefined, { minimumFractionDigits: 2 }),
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: unknown, record: LineItemRow) => (
        <Popconfirm
          title={t('orders.line_item_confirm_delete')}
          onConfirm={() => removeLineItem(record.key)}
          okText={t('app.yes')}
          cancelText={t('app.no')}
        >
          <Button type="link" danger icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <Typography.Title level={4}>
        {t(isEdit ? 'orders.edit_title' : 'orders.create_title')}
      </Typography.Title>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          currency: 'TWD',
          discount_percentage: 0,
          discount_amount: 0,
          tax_percentage: 0,
        }}
      >
        <Form.Item
          name="customer_id"
          label={t('orders.field.customer_id')}
          rules={[{ required: true, message: t('orders.validation.customer_id_required') }]}
        >
          <Select
            showSearch
            filterOption={false}
            onSearch={setCustomerSearch}
            placeholder={t('orders.select_customer')}
            options={customerOptions}
          />
        </Form.Item>

        <Form.Item name="contact_id" label={t('orders.field.contact_id')}>
          <Input placeholder={t('orders.field.contact_id')} />
        </Form.Item>

        <Form.Item name="quote_id" label={t('orders.field.quote_id')}>
          <Input placeholder={t('orders.field.quote_id')} />
        </Form.Item>

        <Form.Item name="order_number" label={t('orders.field.order_number')}>
          <Input maxLength={100} />
        </Form.Item>

        <Form.Item
          name="title"
          label={t('orders.field.title')}
          rules={[
            { required: true, message: t('orders.validation.title_required') },
            { max: 500, message: t('orders.validation.title_max_length') },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="description"
          label={t('orders.field.description')}
          rules={[{ max: 2000, message: t('orders.validation.description_max_length') }]}
        >
          <TextArea rows={3} />
        </Form.Item>

        {isEdit && (
          <Form.Item name="status" label={t('orders.field.status')}>
            <Select
              options={ORDER_STATUSES.map((s) => ({
                value: s,
                label: t(`orders.status_options.${s}`),
              }))}
            />
          </Form.Item>
        )}

        <Form.Item name="order_date" label={t('orders.field.order_date')}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>

        <Space size="large" style={{ width: '100%' }}>
          <Form.Item name="currency" label={t('orders.field.currency')}>
            <Input maxLength={10} style={{ width: 100 }} />
          </Form.Item>
          <Form.Item name="discount_percentage" label={t('orders.field.discount_percentage')}>
            <InputNumber min={0} max={100} />
          </Form.Item>
          <Form.Item name="discount_amount" label={t('orders.field.discount_amount')}>
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item name="tax_percentage" label={t('orders.field.tax_percentage')}>
            <InputNumber min={0} max={100} />
          </Form.Item>
        </Space>

        <Form.Item
          name="shipping_address"
          label={t('orders.field.shipping_address')}
          rules={[{ max: 2000, message: t('orders.validation.shipping_address_max_length') }]}
        >
          <TextArea rows={2} />
        </Form.Item>

        <Form.Item
          name="payment_terms"
          label={t('orders.field.payment_terms')}
          rules={[{ max: 1000, message: t('orders.validation.payment_terms_max_length') }]}
        >
          <TextArea rows={2} />
        </Form.Item>

        <Form.Item
          name="notes"
          label={t('orders.field.notes')}
          rules={[{ max: 2000, message: t('orders.validation.notes_max_length') }]}
        >
          <TextArea rows={2} />
        </Form.Item>

        <Form.Item name="tags" label={t('orders.field.tags')}>
          <Select mode="tags" placeholder={t('orders.field.tags')} />
        </Form.Item>

        {/* Line Items Table */}
        <Card
          title={t('orders.line_items_title')}
          extra={
            <Button type="dashed" icon={<PlusOutlined />} onClick={addLineItem}>
              {t('orders.add_line_item')}
            </Button>
          }
          style={{ marginBottom: 16 }}
        >
          <Table<LineItemRow>
            dataSource={lineItems}
            columns={lineItemColumns}
            rowKey="key"
            pagination={false}
            size="small"
          />
        </Card>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={saving}>
              {t('app.save')}
            </Button>
            <Button onClick={() => navigate(isEdit ? `/orders/${id}` : '/orders')}>
              {t('app.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
